import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtRelative, StatusPill } from "@/lib/workflow";
import {
  Inbox, ClipboardCheck, DollarSign, Activity, ArrowRight, ShieldCheck,
  Clock, Hourglass, AlertTriangle, Send, CheckCircle2,
} from "lucide-react";

/**
 * Vendor home dashboard — at-a-glance KPIs + recent activity timeline.
 * Pulls /vendor/me, /vendor/me/stats, /vendor/me/activity.
 */
export default function VendorHome() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/vendor/me").then((r) => r.data).catch(() => null),
      api.get("/vendor/me/stats").then((r) => r.data).catch(() => null),
      api.get("/vendor/me/activity").then((r) => r.data).catch(() => []),
    ]).then(([m, s, a]) => {
      setMe(m); setStats(s); setActivity(a);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading vendor dashboard…</div>;
  }

  return (
    <div className="space-y-6" data-testid="vendor-home">
      {/* Hero strip */}
      <div className="rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -right-20 -top-24 w-[420px] h-[420px] rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="relative flex items-start gap-5 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-200">Vendor portal</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1" data-testid="vendor-home-title">
              Welcome back{me?.name ? `, ${me.name}` : ""}
            </h1>
            <p className="text-sm text-emerald-100/85 mt-1.5 max-w-2xl">
              Your operational snapshot, recent activity, and one-click access to incoming workflow requests, invoices, and your public profile.
            </p>
          </div>
          {stats?.tier && (
            <span
              data-testid="vendor-home-tier-pill"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider self-start ${
                stats.tier === "preferred"
                  ? "bg-emerald-300 text-emerald-900"
                  : stats.tier === "standard"
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-amber-200 text-amber-900"
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {stats.tier} tier · {stats.reliability_score}/100
            </span>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vendor-home-kpis">
        <Kpi label="Pending acceptance" value={stats?.pending_acceptance ?? 0}
          accent="bg-amber-100 text-amber-700" icon={Hourglass} testId="vendor-kpi-pending"
          link="/vendor/orders" cta="Open orders →" />
        <Kpi label="Active workflows" value={stats?.active_workflows ?? 0}
          accent="bg-blue-100 text-blue-700" icon={Inbox} testId="vendor-kpi-active"
          link="/vendor" cta="View →" />
        <Kpi label="Completed · 30d" value={stats?.completed_30d ?? 0}
          accent="bg-emerald-100 text-emerald-700" icon={ClipboardCheck} testId="vendor-kpi-completed" />
        <Kpi label="Revenue · MTD"
          value={`$${Math.round(stats?.revenue_mtd ?? 0).toLocaleString()}`}
          accent="bg-violet-100 text-violet-700" icon={DollarSign} testId="vendor-kpi-revenue"
          link="/vendor/invoices" cta="Invoices →" />
      </div>

      {/* Program dispatches — compact tile linking to full inbox */}
      <ProgramDispatchesTile />

      {/* Performance + invoices alert */}
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5" data-testid="vendor-perf-card">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 inline-flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> Operational reliability
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3 text-center">
            <PerfTile label="Reliability" value={`${stats?.reliability_score ?? 0}/100`} testId="vendor-perf-reliability" />
            <PerfTile label="Acceptance" value={`${stats?.acceptance_rate ?? 0}%`} testId="vendor-perf-acceptance" />
            <PerfTile label="Fulfillment" value={`${stats?.fulfillment_rate ?? 0}%`} testId="vendor-perf-fulfillment" />
          </div>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Operational metrics only — based on acceptance rate (last 90d) and fulfillment rate. <strong>Never</strong> a measure of clinical quality. Vendors above 75/100 earn Preferred tier.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="vendor-invoices-card">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-violet-700 inline-flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> Receivables
          </div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1.5 tabular-nums">
            ${Math.round(stats?.unpaid_aging ?? 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500">{stats?.unpaid_count ?? 0} unpaid invoices</div>
          <Link
            to="/vendor/invoices"
            data-testid="vendor-invoices-card-link"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900"
          >
            Open invoices <ArrowRight className="w-3 h-3" />
          </Link>
          {stats?.pending_acceptance > 5 && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-[11px] p-2 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {stats.pending_acceptance} requests awaiting your acceptance
            </div>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Recent activity
          </div>
          <Link
            to="/vendor"
            data-testid="vendor-recent-activity-all"
            className="text-xs font-semibold text-blue-700 hover:text-blue-900"
          >
            See all →
          </Link>
        </div>
        {activity.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            No workflow activity yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="vendor-recent-activity">
            {activity.slice(0, 8).map((w) => (
              <li key={w.id} className="px-5 py-3 flex items-center gap-3">
                <Link
                  to={`/vendor/workflows/${w.id}`}
                  className="font-mono text-[11px] font-bold text-slate-500 hover:text-slate-900 w-28 truncate"
                  data-testid={`vendor-activity-${w.id}`}
                >
                  {w.order_id}
                </Link>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{w.requested_workflow || w.service_summary}</div>
                  <div className="text-[11px] text-slate-500 truncate">{w.clinic_name}</div>
                </div>
                <StatusPill status={w.status} />
                <span className="text-[11px] text-slate-400 w-20 text-right">{fmtRelative(w.updated_at || w.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon, testId, link, cta }) {
  const inner = (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 h-full">
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</span>
      </div>
      <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-2">{value}</div>
      {cta && <div className="text-xs font-semibold text-emerald-700 mt-1">{cta}</div>}
    </div>
  );
  if (link) {
    return <Link to={link} data-testid={testId} className="block hover:shadow-sm transition">{inner}</Link>;
  }
  return <div data-testid={testId}>{inner}</div>;
}

function PerfTile({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-xl font-extrabold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}


/**
 * ProgramDispatchesTile — compact summary of the vendor's incoming
 * program-launch dispatches. Reads GET /vendor/dispatches.summary
 * without loading rows (uses ?limit=1 to keep payload tiny).
 * Displays: Active count, Waiting-to-accept count, Overdue count, and
 * a CTA to the full inbox at /vendor/dispatches.
 */
function ProgramDispatchesTile() {
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/vendor/dispatches", { params: { limit: 500 } })
      .then((r) => {
        if (cancelled) return;
        setSummary(r.data?.summary || {});
        const today = new Date().toISOString().slice(0, 10);
        const overdueCount = (r.data?.dispatches || []).filter((d) => {
          if (!d.complete_by) return false;
          const active = ["pending", "accepted", "in_progress", "email_failed"].includes(d.status);
          return active && d.complete_by < today;
        }).length;
        setOverdue(overdueCount);
      })
      .catch(() => { /* dispatch data optional — fail silently */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="vendor-dispatches-tile-loading"
        className="rounded-2xl border border-slate-200 bg-white p-5"
      >
        <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
      </div>
    );
  }

  const pending = summary?.pending || 0;
  const accepted = summary?.accepted || 0;
  const inProgress = summary?.in_progress || 0;
  const emailFailed = summary?.email_failed || 0;
  const active = pending + accepted + inProgress + emailFailed;

  return (
    <div
      data-testid="vendor-dispatches-tile"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 inline-flex items-center gap-1.5">
            <Send className="w-3 h-3" /> Program dispatches
          </div>
          <div className="mt-1 font-display text-lg font-bold text-slate-900 leading-tight">
            {active === 0 ? (
              "No dispatches waiting on you."
            ) : (
              <>
                <span data-testid="vendor-dispatches-tile-active">{active}</span> dispatch{active === 1 ? "" : "es"} need your attention
              </>
            )}
          </div>
        </div>
        <Link
          to="/vendor/dispatches"
          data-testid="vendor-dispatches-tile-link"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
        >
          Open inbox <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <TileStat
          label="Waiting to accept"
          value={pending}
          tone={pending > 0 ? "amber" : "neutral"}
          testId="vendor-dispatches-tile-pending"
        />
        <TileStat
          label="Accepted"
          value={accepted}
          tone="blue"
          testId="vendor-dispatches-tile-accepted"
        />
        <TileStat
          label="In progress"
          value={inProgress}
          tone="violet"
          testId="vendor-dispatches-tile-in-progress"
        />
        <TileStat
          label="Overdue"
          value={overdue}
          tone={overdue > 0 ? "rose" : "emerald"}
          testId="vendor-dispatches-tile-overdue"
          icon={overdue > 0 ? AlertTriangle : CheckCircle2}
        />
      </div>

      {emailFailed > 0 && (
        <div
          data-testid="vendor-dispatches-tile-email-failed"
          className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[11px] text-rose-800"
        >
          <span className="font-semibold">{emailFailed}</span> dispatch{emailFailed === 1 ? "" : "es"} could not be emailed —
          contact <span className="font-mono">info@noderahealth.com</span> to update your delivery address.
        </div>
      )}
    </div>
  );
}

function TileStat({ label, value, tone, testId, icon: Icon }) {
  const map = {
    neutral: "bg-slate-50 border-slate-100 text-slate-900",
    amber:   "bg-amber-50 border-amber-100 text-amber-900",
    blue:    "bg-blue-50 border-blue-100 text-blue-900",
    violet:  "bg-violet-50 border-violet-100 text-violet-900",
    rose:    "bg-rose-50 border-rose-100 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-900",
  };
  const cls = map[tone] || map.neutral;
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="mt-1 flex items-center gap-1.5">
        <div className="font-display font-extrabold text-2xl leading-none tabular-nums">{value}</div>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
    </div>
  );
}
